import React, { useMemo } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { DateTimePicker } from '@material-ui/pickers';
import { DateTime } from 'luxon';

import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import Switch from '@material-ui/core/Switch';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormHelperText from '@material-ui/core/FormHelperText';
import MenuItem from '@material-ui/core/MenuItem';

const renderDateInput = props => (
    <TextField
        variant="outlined"
        fullWidth
        onClick={props.onClick}
        id={props.id}
        value={props.value}
        disabled={props.disabled}
        label={props.label}
        helperText={props.helperText}
        {...props.inputProps}
    />
);

const useStyles = makeStyles(theme => ({
    section: {
        marginBottom: theme.spacing(3)
    },
    formContainer: {
        overflowY: 'scroll',
        height: '100%',
        padding: theme.spacing(3)
    },
    marginTop: {
        marginTop: theme.spacing(3)
    },
    image: {
        maxHeight: 300,
        maxWidth: 300,
        height: 'auto',
        width: '100%'
    },
    imageContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
        padding: theme.spacing(6)
    },
    height: {
        height: '100%'
    },
    settingsContainer: {
        height: '100%',
        padding: theme.spacing(3)
    },
}));

export default function VotingDetails(props) {

    const {
        election,
        handleElection,
        isError,
        getErrorMessage,
        errors,
        groups,
        newElection
    } = props;

    const classes = useStyles();

    const votingStartsAtMessage = useMemo(() => {
        if (isError('voting_starts_at')) return errors['voting_starts_at'];
        return "A blank value will allow voting to occur at anytime during the election cycle."
    }, [errors]);

    const votesPerUserMessage = useMemo(() => {
        if (isError('votes_per_user')) return errors['votes_per_user'];
        return "0 will allow users to vote for all proposals"
    }, [errors]);

    const initialStartDate = useMemo(() => {
        if (election.proposals_end_at) return election.proposals_end_at;
        else if (election.proposals_start_at) return election.proposals_start_at.plus({weeks: 2});
        else return election.starts_at.plus({weeks: 2});

    }, [election.starts_at, election.proposals_start_at]);

    const initialEndDate = useMemo(() => {

        return initialStartDate.plus({weeks: 2});

    }, [initialStartDate]);

    return (
        <Grid container direction="row" className={newElection ? classes.height : null}>
            {newElection &&
                <Grid item xs className={classes.height}>
                    <div className={classes.imageContainer}>
                        <img className={classes.image} src='/images/step-voting.png' alt="voting-graphic" />
                    </div>
                </Grid>
            }
            <Grid item xs={newElection ? 7 : 12} className={newElection ? classes.formContainer : classes.settingsContainer}>
            {newElection &&
                <Typography variant='h6' gutterBottom>
                    Voting Details
                </Typography>
            }
                <FormControl>
                    <FormControlLabel
                        margin="normal"
                        control={
                            <Switch
                                checked={election.voting_active}
                                onChange={handleElection('voting_active')}
                                color="primary"
                                value="voting_active"
                            />
                        }
                        label="Allow users to vote on proposals?"
                        labelPlacement="end"
                    />
                    <FormHelperText>
                        When disabled, administrators will still be able to cast votes for users
                    </FormHelperText>
                </FormControl>
                <div className={classes.section}>
                    <Typography variant='subtitle2' gutterBottom>
                        Select the group of users that can vote.
                    </Typography>
                    <TextField
                        id="input-voting-group-id"
                        select
                        value={election.voting_group_id}
                        onChange={handleElection('voting_group_id')}
                        variant="outlined"
                        fullWidth
                        error={isError('voting_group_id')}
                        helperText={getErrorMessage('voting_group_id')}
                        disabled={! election.voting_active}
                        >
                        <MenuItem value={0}>
                            All Users
                        </MenuItem>
                        {groups.map(group => (
                            <MenuItem key={group.id} value={group.id}>
                                {group.title}
                            </MenuItem>
                        ))}
                    </TextField>
                </div>
                <div className={classes.section}>
                    <Typography variant='subtitle2' gutterBottom>
                        What date and time should users be allowed to begin voting?
                    </Typography>
                    <DateTimePicker
                        clearable
                        label="Voting Start Date"
                        inputVariant="outlined"
                        value={election.voting_starts_at}
                        onChange={handleElection('voting_starts_at')}
                        variant="outlined"
                        TextFieldComponent={renderDateInput}
                        minutesStep={15}
                        disabled={! election.voting_active}
                        helperText={votingStartsAtMessage}
                        initialFocusedDate={initialStartDate}
                    />
                </div>
                <div className={classes.section}>
                    <Typography variant='subtitle2' gutterBottom>
                        What date and time should voting end?
                    </Typography>
                    <DateTimePicker
                        clearable
                        label="Voting End Date"
                        inputVariant="outlined"
                        value={election.voting_ends_at}
                        onChange={handleElection('voting_ends_at')}
                        variant="outlined"
                        TextFieldComponent={renderDateInput}
                        minutesStep={15}
                        disabled={! election.voting_active}
                        initialFocusedDate={initialEndDate}
                    />
                </div>
                <div className={classes.section}>
                    <Typography variant='subtitle2' gutterBottom>
                        How may votes can a user make?
                    </Typography>
                    <TextField
                        id="input-votes-per-user"
                        value={election.votes_per_user}
                        onChange={handleElection('votes_per_user')}
                        fullWidth
                        type="number"
                        error={isError('votes_per_user')}
                        helperText={votesPerUserMessage}
                        variant="outlined"
                        disabled={! election.voting_active}
                    />
                </div>
                <FormControlLabel
                    margin="normal"
                    control={
                        <Switch
                            checked={election.voting_starting_notifications}
                            onChange={handleElection('voting_starting_notifications')}
                            color="primary"
                            value="voting_starting_notifications"
                        />
                    }
                    label="Should Voting Group Members be sent a notification before voting begins?"
                    labelPlacement="end"
                    disabled={(!election.voting_active)}
                />
                <FormControlLabel
                    margin="normal"
                    control={
                        <Switch
                            checked={election.voting_ending_notifications}
                            onChange={handleElection('voting_ending_notifications')}
                            color="primary"
                            value="voting_ending_notifications"
                        />
                    }
                    label="Should Voting Group Members receive a warning email before voting ends?"
                    labelPlacement="end"
                    disabled={(!election.voting_active)}
                />
            </Grid>
        </Grid>
    )
}
